import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Paperclip, Mic, FileText, ExternalLink } from "lucide-react";

const kindIcon = { audio: Mic, note: FileText, file: Paperclip };
const kindLabel = { audio: "Audio", note: "Note", file: "Fichier" };

export default function LearnerSubmissions() {
  const [items, setItems] = useState([]);
  const [missions, setMissions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState({ note: "", mission_id: "none", session_id: "none", file: null, filePath: "", fileName: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/learner/submissions").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/learner/missions").then(({ data }) => setMissions(data));
    api.get("/learner/sessions").then(({ data }) => setSessions(data));
  }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/learner/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, file: f, filePath: data.path, fileName: f.name }));
      toast.success("Fichier téléversé");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.note.trim() && !form.filePath) return toast.error("Ajoutez une note ou un fichier");
    setSaving(true);
    try {
      const payload = {
        note: form.note,
        mission_id: form.mission_id === "none" ? null : form.mission_id,
        session_id: form.session_id === "none" ? null : form.session_id,
        file_path: form.filePath || null,
        original_filename: form.fileName || null,
        kind: form.filePath ? (form.fileName.match(/\.(mp3|m4a|wav|ogg)$/i) ? "audio" : "file") : "note",
      };
      await api.post("/learner/submissions", payload);
      toast.success("Travail déposé — votre coach a été notifiée");
      setForm({ note: "", mission_id: "none", session_id: "none", file: null, filePath: "", fileName: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce dépôt ?")) return;
    await api.delete(`/learner/submissions/${id}`);
    load();
  };

  return (
    <div className="lg:ml-56">
      <h1 className="font-serif-accent text-3xl sm:text-4xl">Mon espace de dépôt</h1>
      <p className="text-[#5C6561] mt-1 mb-5 text-sm">Déposez vos travaux (note écrite, audio, fichier) — votre coach les verra automatiquement.</p>

      <form onSubmit={submit} className="dec-card p-5 space-y-4 dec-side-accent">
        <div>
          <Label>Votre note (optionnel)</Label>
          <Textarea
            data-testid="submission-note"
            placeholder="Ex : Voici mon enregistrement d'introduction, j'ai eu du mal avec les -ed…"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            rows={3}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Mission associée (optionnel)</Label>
            <Select value={form.mission_id} onValueChange={(v) => setForm({ ...form, mission_id: v })}>
              <SelectTrigger data-testid="submission-mission-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {missions.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Séance associée (optionnel)</Label>
            <Select value={form.session_id} onValueChange={(v) => setForm({ ...form, session_id: v })}>
              <SelectTrigger data-testid="submission-session-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {sessions.map(s => <SelectItem key={s.id} value={s.id}>Séance {s.number} — {s.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Fichier (audio, PDF, image…) (optionnel)</Label>
          <div className="flex items-center gap-2 mt-1">
            <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="submission-file-input" accept="audio/*,video/*,image/*,application/pdf,text/plain" />
            <Button type="button" variant="outline" className="bg-white" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Upload className="w-4 h-4 mr-2" /> {uploading ? "Téléversement…" : "Choisir un fichier"}
            </Button>
            {form.fileName && <span className="text-xs text-[#2A5B52] truncate">✓ {form.fileName}</span>}
          </div>
          <p className="text-[11px] text-[#5C6561] mt-1">Vous pouvez enregistrer un audio depuis votre téléphone puis l'importer ici.</p>
        </div>
        <Button
          data-testid="submission-submit"
          type="submit"
          disabled={saving}
          className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white"
        >
          Déposer mon travail
        </Button>
      </form>

      <h2 className="font-serif-accent text-2xl mt-8 mb-3">Mes dépôts</h2>
      <div className="space-y-2">
        {items.length === 0 && <div className="dec-card p-6 text-center text-[#5C6561] text-sm">Aucun dépôt pour le moment.</div>}
        {items.map((s) => {
          const Icon = kindIcon[s.kind] || Paperclip;
          const mission = missions.find(m => m.id === s.mission_id);
          const sess = sessions.find(x => x.id === s.session_id);
          const token = localStorage.getItem("dec_learner_token");
          const url = s.file_path ? `${API_BASE}/files/${s.file_path}` : null;
          return (
            <div key={s.id} className="dec-card p-4" data-testid={`submission-item-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-[#5C6561]">{kindLabel[s.kind] || "Dépôt"} • {new Date(s.created_at).toLocaleString("fr-FR")}</div>
                  {(mission || sess) && (
                    <div className="text-xs text-[#5C6561] mt-0.5">
                      {mission && <>Mission : <strong>{mission.title}</strong></>}
                      {mission && sess && " · "}
                      {sess && <>Séance {sess.number}</>}
                    </div>
                  )}
                  {s.note && <div className="mt-2 text-sm whitespace-pre-wrap">{s.note}</div>}
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#D95D39] font-medium">
                      <ExternalLink className="w-3 h-3" /> {s.original_filename || "Ouvrir le fichier"}
                    </a>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`submission-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
